<template>
  <form id="job-create-form" class="flex flex-col flex-1 min-h-0" @submit="onCreateSubmit">
    <div class="overflow-y-auto flex-1 min-h-0 px-6 py-4 grid gap-4">
      <!-- QUICK CAPTURE (design 165) — the phone is ringing, and the form asks
           only what a job cannot exist WITHOUT, in the call's own order:
           WHO (the client at an address), WHAT (the type), then WHERE THE DEAL
           IS at the end of the call (four cards). Crew, paper, program rows
           and notes are filled on the page that opens — the page
           is the continuation of the call.
           (label→input runs at gap-1.5 — `space-y-0` kills the base FormItem's
           `space-y-2`, which STACKED with the grid gap for a 16px fog.) -->

      <!-- ══ CLIENT ══ -->
      <div class="grid gap-3">
        <!-- A LOCKED address (spawn door) is read back, never re-asked. -->
        <div v-if="props.lockLocation" class="grid gap-1.5" data-testid="locked-customer">
          <Label class="text-sm font-medium">{{ t('customers.customer') }}</Label>
          <p class="text-sm font-medium">{{ customerLabelText }}</p>
        </div>
        <FormField v-else v-slot="{ value }" name="serviceLocationId">
          <FormItem class="grid gap-1.5 space-y-0">
            <FormLabel class="text-sm font-medium">{{ t('customers.customer') }}</FormLabel>
            <FormControl>
              <CustomerPicker
                :model-value="value === '__new__' ? null : (value || null)"
                allow-create
                :placeholder="t('customers.pickPlaceholder')"
                @update:model-value="onLocationPicked"
                @select="onLocationSelect"
                @new-address="onStartCustomerCreate"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <CustomerCreateBlock
          v-if="creatingCustomer"
          :kind="values.newCustomer?.kind ?? 'contact'"
          :dedup="dedup"
          @cancel="onCancelCustomerCreate"
        />
        <!-- The lead guard: a live lead at this address means the capture is
             probably a wrong turn — continue on the existing job, or branch
             a NEW scope off its completed look-see. It renders only when it
             has something to say, and never when the form was opened FROM
             that job. -->
        <LeadGuardBanner
          v-if="!props.originJobId && !props.lockLocation && !creatingCustomer"
          :service-location-id="guardLocationId"
          :branched="!!branchOriginJobId"
          @branch="onBranch"
          @unbranch="branchOriginJobId = null"
          @navigate="$emit('cancel')"
        />
      </div>

      <!-- ══ TYPE — required on every card: a caller names the work. ══ -->
      <FormField v-slot="{ value }" name="templateId">
        <FormItem class="grid gap-1.5 space-y-0">
          <FormLabel class="text-sm font-medium">{{ t('jobs.fields.jobType') }}</FormLabel>
          <FormControl>
            <JobTemplateCombobox
              :model-value="value || null"
              allow-create
              :placeholder="t('jobs.quickCapture.typePlaceholder')"
              data-testid="job-type"
              @update:model-value="onPickTemplate"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- ══ SAISON — one line, only when the type has a season: the type's
           served default, « changer » = the one season menu. The run is born
           on demand server-side; nothing here predicts a date. ══ -->
      <div v-if="season" class="flex items-center gap-2 text-sm" data-testid="intake-season">
        <span class="text-muted-foreground">{{ t('jobs.season.label') }}</span>
        <span class="font-medium">{{ season.label }}</span>
        <Popover v-if="seasonChoiceList.length > 1" v-model:open="seasonMenuOpen">
          <PopoverTrigger as-child>
            <button type="button" class="link-ink text-sm" data-testid="intake-season-change">{{ t('jobs.season.change') }}</button>
          </PopoverTrigger>
          <PopoverContent align="start" class="w-auto p-1.5">
            <SeasonChoiceList :choices="seasonChoiceList" :selected-key="`${season.workPeriodId}:${season.seasonYear}`" @pick="onPickSeason" />
          </PopoverContent>
        </Popover>
      </div>

      <!-- ══ LA SUITE — the deal's state at the end of the call, four cards,
           the type's default preselected (re-read on every type change). A
           shop that does not quote has one lane and sees no cards. ══ -->
      <div v-if="orgStore.salesEnabled" class="grid gap-1.5">
        <Label class="text-sm font-medium">{{ t('jobs.intake.next') }}</Label>
        <RadioGroup
          :model-value="intake"
          class="grid grid-cols-2 gap-2"
          data-testid="intake-cards"
          @update:model-value="onPickIntake"
        >
          <label
            v-for="choice in INTAKE_CHOICES"
            :key="choice"
            class="flex cursor-pointer items-start gap-2.5 rounded-lg border p-3"
            :class="intake === choice ? selectedClass : baseClass"
            :data-testid="`intake-card-${choice}`"
            :data-selected="intake === choice ? 'true' : 'false'"
          >
            <RadioGroupItem :value="choice" class="mt-0.5" />
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-medium">{{ t(`jobs.intake.cards.${choice}.label`) }}</span>
              <span class="block text-xs text-muted-foreground">{{ t(`jobs.intake.cards.${choice}.caption`) }}</span>
            </span>
          </label>
        </RadioGroup>
      </div>

      <!-- ══ COMBIEN ? — only under « Vendu »: the job is BORN won, so the
           money answer rides the create (the shared face every win door
           wears; the type picked above drives its « Soumission type » row). ══ -->
      <div v-if="orgStore.salesEnabled && intake === 'won'" class="grid gap-1.5" data-testid="intake-agreement">
        <Label class="text-sm font-medium">{{ t('jobs.agreement.section') }}</Label>
        <CommitmentFields
          :mode="values.agreementMode ?? null"
          :amount="values.agreedAmount ?? null"
          :label="values.agreedLabel"
          :taxable="values.agreedTaxable"
          :kind="values.agreedKind"
          :description="values.agreedDescription"
          :job-template-id="values.templateId || null"
          :label-placeholder="templateName || null"
          :error="submitCount > 0 ? errors.agreedAmount : undefined"
          @update:mode="setFieldValue('agreementMode', $event)"
          @update:amount="setFieldValue('agreedAmount', $event ?? undefined)"
          @update:label="setFieldValue('agreedLabel', $event)"
          @update:taxable="setFieldValue('agreedTaxable', $event)"
          @update:kind="setFieldValue('agreedKind', $event)"
          @update:description="setFieldValue('agreedDescription', $event)"
          @update:template-state="templateState = $event"
        />
      </div>

      <!-- « Paiement avant travaux » — the sold birth's question, prefilled
           from the customer (visible, never a silent server force). -->
      <label v-if="paymentFirstAsked" class="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium" data-testid="intake-payment-first">
        {{ t('jobs.paymentFirst.label') }}
        <Switch :checked="paymentFirst" @update:checked="paymentFirst = $event === true" />
      </label>
    </div>

    <DialogFooter class="shrink-0 px-6 py-4 border-t gap-3">
      <Button type="button" variant="ghost" @click="$emit('cancel')">{{ t('common.cancel') }}</Button>
      <Button type="submit" :loading="isCreating" :disabled="blocksCreate" data-testid="submit">
        {{ t('jobs.intake.createAndOpen') }}
      </Button>
    </DialogFooter>
  </form>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import type { Resource } from '@/api/resources'

import { useJobs } from '@/composables/useJobs'
import { useJobTemplates } from '@/composables/useJobTemplates'
import { useServiceLocations } from '@/composables/useServiceLocations'
import { useSchemaJob, type JobFormValues } from '@/composables/useSchemaJob'
import { useJobIntake, INTAKE_CHOICES, type IntakeChoice, type IntakePrefills, type JobCreated } from '@/composables/useJobIntake'
import CommitmentFields, { type TemplateFaceState } from '@/components/shared/CommitmentFields.vue'
import { useSeasonCatalog } from '@/composables/useSeasonCatalog'
import { seasonChoices, type SeasonChoice } from '@/lib/seasons'
import SeasonChoiceList from '@/components/shared/SeasonChoiceList.vue'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useOrganizationStore } from '@/stores/organization'
import {
  useInlineCustomerCreate, blankCustomerDraft, blankAddressDraft,
} from '@/composables/useInlineCustomerCreate'
import { useCustomerDedup } from '@/composables/useCustomerDedup'
import { fullName, splitFullName } from '@/lib/names'
import { derivedJobTitle } from '@/lib/jobTitle'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { DialogFooter } from '@/components/ui/dialog'
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import CustomerPicker from '@/components/shared/CustomerPicker.vue'
import CustomerCreateBlock from '@/components/shared/CustomerCreateBlock.vue'
import JobTemplateCombobox from '@/components/shared/JobTemplateCombobox.vue'
import LeadGuardBanner from '@/components/shared/LeadGuardBanner.vue'

// Prefills, all VISIBLE and editable once seeded (never a silent server force):
//   initialIntake            — a card already answered by the door that opened us
//   initialTemplateId        — pre-picks a job type
//   initialServiceLocationId — the spawn doors arrive with the ADDRESS known
//                              (the customer follows from it)
//   originJobId              — the chain: the job this one is opened from (born
//                              assessed server-side if its look-see is done)
const props = defineProps<{
  initialIntake?: IntakeChoice
  initialTemplateId?: string
  initialServiceLocationId?: string
  originJobId?: string
  // The SPAWN doors (« Nouveau job ici » on a job or a location page): the
  // address is the answer, not a question — the picker gives way to one
  // read-back line, the lead guard stays out (it guards THIS address).
  lockLocation?: boolean
}>()
// The form runs silent: the host toasts the notice and lands on the job.
const emit = defineEmits<{ success: [jobId: string, created: JobCreated]; cancel: [] }>()

const { t } = useI18n()
const orgStore = useOrganizationStore()
const intakePrefills: IntakePrefills = props
const schema = useSchemaJob()
const { create } = useJobs()
const { fetchServiceLocation } = useServiceLocations()
const { fetchJobTemplate, currentJobTemplate, templateSteps } = useJobTemplates()
const { ensureCustomerLocation, resetInlineCreate, createdContactId } = useInlineCustomerCreate()

const isCreating = ref(false)

const baseClass = 'border-input bg-background'
const selectedClass = 'border-state-ink bg-state-wash'

// The customer's SERVED label for the picked address — the job title reads it, so
// a picked customer's name never has to be recomposed from parts.
const customerLabelText = ref('')

const { handleSubmit, setFieldValue, resetForm, values, errors, submitCount } = useForm<JobFormValues>({
  validationSchema: computed(() => toTypedSchema(schema.value)),
  initialValues: {
    serviceLocationId: '',
    templateId: '',
    intakeLane: 'evaluation',
    agreedLabel: '',
    agreedTaxable: true,
    agreedKind: 'labor',
    agreedDescription: '',
  },
})

const creatingCustomer = computed(() => values.serviceLocationId === '__new__')

// Match-at-entry (doc/FEATURE_CUSTOMER_DEDUP.md): owned HERE because submit and
// the create chain are — the block only renders its questions.
const dedup = useCustomerDedup({
  address: () => values.newAddress,
  cell: () => values.newCustomer?.cellPhone ?? '',
})

// [C'est Ginette] PREFILLS the draft with the REAL identity (visible prefill,
// editable after « changer ») — the typed half-name would otherwise leak into
// the derived job title and the company branch's hasPerson gate, while the
// block collapses these fields behind the resolved line.
watch(() => dedup.personAnswer.value, (answer) => {
  if (answer?.use !== 'existing' || !values.newCustomer) return
  setFieldValue('newCustomer.firstName', answer.candidate.first_name)
  setFieldValue('newCustomer.lastName', answer.candidate.last_name)
})

// The guard watches the picked location (a fresh inline address can't have a
// lead yet, so '__new__' reads as nothing).
const guardLocationId = computed(() => {
  const id = values.serviceLocationId
  return id && id !== '__new__' ? id : null
})

const {
  intake, templateName, season,
  branchOriginJobId, effectiveOriginJobId,
  onBranch, pickIntake, seedFromTemplate, setSeason, createAttributes, resetIntake,
} = useJobIntake(intakePrefills, guardLocationId)

// « Combien ? »'s template face, as CommitmentFields reports it — the submit
// gate while « Soumission type » is chosen but its numbers are incomplete
// (the schema guards the other two answers).
const templateState = ref<TemplateFaceState>({ status: 'none', valid: false, total: null, lines: [], proposalTemplateId: null })
const blocksCreate = computed(() =>
  intake.value === 'won' && values.agreementMode === 'template' && !templateState.value.valid)

// THE SEASON MENU — the type's seasons × the next three years, from the
// shared catalog; the served default is the line's first answer.
const catalog = useSeasonCatalog()
if (orgStore.currentOrganizationId) void catalog.load(orgStore.currentOrganizationId)
const seasonMenuOpen = ref(false)
const seasonChoiceList = computed(() =>
  seasonChoices(catalog.seasonsForType(values.templateId || null)))
const onPickSeason = (choice: SeasonChoice) => {
  setSeason({ workPeriodId: choice.workPeriodId, seasonYear: choice.seasonYear, label: choice.label })
  seasonMenuOpen.value = false
}

// The schema's mirror of the card — the agreement rule scopes itself to
// « Vendu » through it (validation must not block a lead on an amount).
watch(intake, lane => setFieldValue('intakeLane', lane, false), { immediate: true })

const isIntakeChoice = (value: unknown): value is IntakeChoice =>
  typeof value === 'string' && (INTAKE_CHOICES as readonly string[]).includes(value)
const onPickIntake = (value: unknown) => {
  if (isIntakeChoice(value)) pickIntake(value)
}

// ── WHO + WHERE: one control, three states ────────────────────────────────
// S0 empty (submit blocked) · S1 an address resolved · S3 a new address being
// born with its customer. There is no orphan state to handle: search matches
// customer NAMES only, so a location nobody owns cannot be surfaced — the one way
// a customerless location arrives is a prefill, which reads as itself and submits
// (its title falling back to the address; repairing it is « changer le client »
// on the job page, not intake's job).
const onLocationPicked = (id: string | null) => {
  setFieldValue('serviceLocationId', id ?? '')
  if (id) {
    setFieldValue('newCustomer', undefined)
    setFieldValue('newAddress', undefined)
  }
}

const onLocationSelect = (location: Resource<'service_location'> | null) => {
  customerLabelText.value = labelFor(location)
  applyPayerDefault(location)
}

// « PAIEMENT AVANT TRAVAUX » — asked only where the job is born SOLD (« Vendu »,
// or every job of a shop that does not quote), prefilled VISIBLY from the
// picked customer's default (served on the location, company first). A
// customer created inline has no default yet: off.
const paymentFirst = ref(false)
const paymentFirstAsked = computed(() => !orgStore.salesEnabled || intake.value === 'won')
const applyPayerDefault = (location: Resource<'service_location'> | null | undefined) => {
  paymentFirst.value = location?.attributes.customer_payment_first === true
}

// The SERVED label (company-first, agreeing with the job's own customer_name) —
// never recomposed here, or the picker and the job it creates would disagree about
// what the customer is called. A customerless location (only reachable as a
// prefill) falls back to its ADDRESS so the job is still named after something
// real instead of carrying the job type alone.
const labelFor = (location: Resource<'service_location'> | null | undefined): string =>
  location?.attributes.customer_label || location?.attributes.display_name || ''

// « + Nouvelle adresse » — the typed term is an editable prefill: it lands in the
// person's names on the contact branch, or in the company's name if it reads like
// a company was typed. splitFullName is a guess and says so by staying editable.
const onStartCustomerCreate = (term: string) => {
  const { firstName, lastName } = splitFullName(term)
  setFieldValue('serviceLocationId', '__new__')
  customerLabelText.value = ''
  setFieldValue('newCustomer', { ...blankCustomerDraft(), firstName, lastName })
  setFieldValue('newAddress', blankAddressDraft())
}

const onCancelCustomerCreate = () => {
  setFieldValue('newCustomer', undefined)
  setFieldValue('newAddress', undefined)
  setFieldValue('serviceLocationId', '')
  customerLabelText.value = ''
  // The questions belonged to the abandoned draft.
  dedup.reset()
}

// The form fetches; useJobIntake decides what the type MEANS (it stays pure):
// the name for the title, the program for a sales-off shop, and the card its
// « évaluation par défaut » flag preselects.
const onPickTemplate = async (id: string | null) => {
  setFieldValue('templateId', id ?? '')
  if (!id) {
    seedFromTemplate([], '')
    return
  }
  const template = (await fetchJobTemplate(id)) ?? currentJobTemplate.value
  const served = template?.attributes.default_season
  seedFromTemplate(templateSteps.value, template?.attributes.name ?? '', template?.attributes.evaluation_by_default,
    served ? { workPeriodId: served.work_period_id, seasonYear: served.season_year, label: served.label } : null)
}

onMounted(async () => {
  // A prefill (spawn door) hands us the ADDRESS — the picker hydrates its own
  // label from the id, and the title reads the served customer_label, so
  // nothing has to be composed from parts here.
  if (props.initialServiceLocationId) {
    setFieldValue('serviceLocationId', props.initialServiceLocationId)
    const location = await fetchServiceLocation(props.initialServiceLocationId)
    customerLabelText.value = labelFor(location)
    applyPayerDefault(location)
  }
  if (props.initialTemplateId) await onPickTemplate(props.initialTemplateId)
})

const onCreateSubmit = handleSubmit(async (formValues) => {
  isCreating.value = true
  try {
    // ── The chain, all of it memoized: a failure on the LAST call must not mint a
    // second company AND a second person when the dispatcher fixes a field and
    // presses « Créer » again.
    let slId = formValues.serviceLocationId

    if (slId === '__new__') {
      if (!formValues.newCustomer || !formValues.newAddress) return
      // ⭐ The gate (design 170): an address that has not answered for its
      // current text is asked now, inside the create — the fast typist never
      // tabbed out. Then the state table's submit column: a raised question
      // blocks — the prompt is on screen saying why. Fail-soft states pass.
      await dedup.ensureAnswered()
      if (dedup.blocksSubmit.value) return
      // [C'est Ginette]: the existing contact IS this person — seed the memo so
      // ensureContact returns her instead of minting a duplicate.
      if (dedup.chosenContactId.value) createdContactId.value = dedup.chosenContactId.value
      const made = await ensureCustomerLocation(formValues.newCustomer, formValues.newAddress, {
        house: dedup.house.value,
        releaseConflictingCell: dedup.releaseCell.value,
        existingLocationId: dedup.chosenLocationId.value,
      })
      if (!made) return
      slId = made
    }

    // The title, DERIVED: the type names the work and the customer names whose
    // it is. Editable afterwards on the job page. A customer born in this same
    // submit has no served label to read yet, so it comes off the draft.
    // ⚠ COMPANY FIRST, matching `Job#customer_name`.
    const customer = formValues.newCustomer
      ? (formValues.newCustomer.kind === 'company'
        ? formValues.newCustomer.companyName.trim()
        : fullName({ first_name: formValues.newCustomer.firstName, last_name: formValues.newCustomer.lastName }))
      : customerLabelText.value
    const derivedTitle = derivedJobTitle(templateName.value, customer)
      || t('jobs.quickCapture.untitled')

    const chosen = intake.value
    const result = await create({
      service_location_id: slId,
      title: derivedTitle,
      job_template_id: formValues.templateId,
      ...(effectiveOriginJobId.value ? { origin_job_id: effectiveOriginJobId.value } : {}),
      // THE CARD, as the keys the server already honours (useJobIntake) — and
      // « Vendu »'s money answer with it, so the job is BORN won.
      ...createAttributes(orgStore.salesEnabled, {
        draft: {
          mode: formValues.agreementMode ?? 'priced',
          amount: formValues.agreedAmount,
          label: formValues.agreedLabel,
          taxable: formValues.agreedTaxable,
          kind: formValues.agreedKind,
          description: formValues.agreedDescription,
        },
        templateLines: templateState.value.lines,
        proposalTemplateId: templateState.value.proposalTemplateId,
      }),
      // « Paiement avant travaux » — sent only where the form ASKED (a sold
      // birth); every other card lets the server stamp the payer's default.
      ...(paymentFirstAsked.value ? { payment_first: paymentFirst.value } : {}),
    }, { silent: true })

    if (result) {
      wipe()
      emit('success', result.id, {
        intake: chosen,
        templateId: formValues.templateId,
        notice: t(chosen === 'lead' ? 'jobs.leadCreated' : chosen === 'won' ? 'pipeline.card.wonVerbalToast' : 'jobs.createSuccess'),
      })
    }
  } finally {
    isCreating.value = false
  }
})

// A closed conversation is over — the next open starts clean (the wipe law). The
// inline-create memo is reset HERE and nowhere else: between retries it is what
// stops a second company being minted.
const wipe = () => {
  resetForm()
  resetIntake()
  resetInlineCreate()
  dedup.reset()
  customerLabelText.value = ''
  paymentFirst.value = false
  templateState.value = { status: 'none', valid: false, total: null, lines: [], proposalTemplateId: null }
}

defineExpose({ reset: wipe })
</script>
